import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import StyledActionButton from 'common/styled/StyledActionButton/StyledActionButton';
import ExternalHeader from 'pages/plasticAssessment/components/Header/Header';
import Company from 'types/Company';

import GreenButton from '../../../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../../../components/CustomLoader/CustomLoader';
import GoodPracticeDetailsDialog from '../../../../dialogs/GoodPracticeDetailsDialog/GoodPracticeDetailsDialog';
import { ReduxState } from '../../../../redux/types';
import GoodPractice from '../../../../types/GoodPractice';
import api from '../../../../utils/api';
import { useDialog } from '../../../../utils/dialog';
import { downloadFile } from '../../../../utils/download';
import snackbar from '../../../../utils/snackbar';
import { ActionTableCell, FlexContainer, StyledButton } from './styled';

interface Props {
    companyData: Company;
    companyId: number;
    assessmentYear: number | null;
    tabsRender: JSX.Element;
    fullWidthSelector: boolean;
    exportWholePdf: () => void;
    setIsCompanyDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const GoodPracitesTab = (props: Props) => {
    const { companyData, assessmentYear, companyId, tabsRender, fullWidthSelector, exportWholePdf, setIsCompanyDialogOpen } = props;

    const userInfo = useSelector((state: ReduxState) => state.user);
    const isAdmin = userInfo.type === 'admin';

    const realCompanyId = companyId || userInfo.companyId;

    const { t } = useTranslation();
    const [openDialog, closeDialog] = useDialog();

    const [isLoading, setIsLoading] = useState(true);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

    const [gps, setGps] = useState<GoodPractice[]>([]);
    const [selectedGpId, selectGpId] = useState(0);

    const getData = useCallback(() => {
        api.request('/companies/' + realCompanyId + '/gps').then((res) => {
            setGps(res);
            setIsLoading(false);
        });
    }, [realCompanyId]);

    const openDetailsDialog = useCallback((certificateId: number) => {
        selectGpId(certificateId);
        setIsDetailsDialogOpen(true);
    }, []);

    const closeDetailsDialog = useCallback(() => {
        setIsDetailsDialogOpen(false);
    }, []);

    const deleteGp = useCallback((gpId: number) => {
        api.request('/companies/' + realCompanyId + '/gps/' + gpId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.good_practice_delete.success'));
            getData();
        });
    }, [getData, realCompanyId, t]);

    const deleteGpDialog = useCallback((gpId: number) => {
        openDialog({
            title: t('dialogs.good_practice_delete.title'),
            message: t('dialogs.good_practice_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteGp(gpId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteGp, openDialog, t]);

    const archiveGp = useCallback((gpId: number, archive: boolean) => {
        api.request('/companies/' + realCompanyId + '/gps/' + gpId + '/' + (archive ? 'archive' : 'disarchive'), 'POST').then(() => {
            snackbar.success(t('dialogs.good_practice_' + (archive ? 'archived' : 'disarchived') + '.success'));
            getData();
        });
    }, [getData, realCompanyId, t]);

    const exportData = useCallback((format: 'csv' | 'pdf') => {
        api.request('/companies/' + companyId + '/gps/export/' + format, 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'export.' + format);
        });
    }, [companyId]);

    useEffect(() => {
        getData();
    }, [getData]);

    if (isLoading) {
        return (
            <>
                <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
                <FlexContainer>
                    {tabsRender}
                </FlexContainer>
                <CustomLoader />
            </>
        );
    }

    return (
        <>
            <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
            {fullWidthSelector && (
                <div style={{ marginBottom: '14px' }}>
                    {tabsRender}
                </div>
            )}
            <FlexContainer>
                {!fullWidthSelector && <>{tabsRender}</>}

                <div>
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('csv')} >
                        {t('pages.plastic_assessment.export_csv')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('pdf')} >
                        {t('pages.plastic_assessment.export_pdf')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportWholePdf()} >
                        {t('pages.plastic_assessment.export_whole_pdf')}
                    </StyledActionButton>

                    {(!isAdmin && userInfo.type !== 'certifier') && (
                        <div style={{ marginTop: '4px', textAlign: 'right' }}>
                            <GreenButton variant='outlined' onClick={() => openDetailsDialog(0)} >
                                {t('pages.good_practices.new_gp')}
                            </GreenButton>
                        </div>
                    )}
                </div>

            </FlexContainer>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                <Trans i18nKey={'pages.good_practices.info_message'}>
                    _<AlertTitle>_</AlertTitle>_
                </Trans>
            </Alert>

            <TableContainer component={Paper} style={{ marginTop: '20px' }} >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('pages.good_practices.table.title')}</TableCell>
                            <TableCell>{t('pages.good_practices.table.description')}</TableCell>
                            {isAdmin && (
                                <TableCell>{t('pages.good_practices.table.is_public')}</TableCell>
                            )}
                            <TableCell>{t('pages.generic.table.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {gps.map(gp => {
                            return (
                                <TableRow key={gp.id}>
                                    <TableCell>{gp.title}</TableCell>
                                    <TableCell>{gp.description}</TableCell>
                                    {isAdmin && (
                                        <TableCell>{t('pages.good_practices.table.is_public_' + (gp.isPublic ? 'yes' : 'no'))}</TableCell>
                                    )}

                                    <ActionTableCell>
                                        {!isAdmin ? (
                                            <>
                                                <StyledButton variant='outlined' color='primary' onClick={() => openDetailsDialog(gp.id)}>
                                                    {t('pages.generic.table.details')}
                                                </StyledButton>{' '}
                                                {userInfo.type !== 'certifier' && (
                                                    <StyledButton variant='outlined' color='secondary' onClick={() => deleteGpDialog(gp.id)}>
                                                        {t('pages.generic.table.delete')}
                                                    </StyledButton>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {gp.isPublic ? (
                                                    <StyledButton variant='outlined' color='primary' onClick={() => archiveGp(gp.id, false)}>
                                                        Rimuovi BP da archivio
                                                    </StyledButton>
                                                ) : (
                                                    <StyledButton variant='outlined' color='primary' onClick={() => archiveGp(gp.id, true)}>
                                                        Archivia GP
                                                    </StyledButton>
                                                )}
                                            </>
                                        )}

                                    </ActionTableCell>

                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <GoodPracticeDetailsDialog
                open={isDetailsDialogOpen}
                onClose={closeDetailsDialog}
                companyId={realCompanyId}
                gpsId={selectedGpId}
                updateData={getData}
            />
        </>
    );
};

export default GoodPracitesTab;
